import * as React from 'react';
import { Link } from 'react-router-dom';
import ServiceItem from '@app/components/ServiceItem/ServiceItem';
import SubService from '@app/components/SubService/SubService';
import { connect, useSelector } from 'react-redux';
import { AppState } from '@app/store';
import ServiceGroupItem from '@app/components/ServiceGroupItem/ServiceGroupItem';
import {
  clearExpandedElements,
  addExpandedElement,
} from '@app/store/catalogue/actions';
import { setServicesHeight } from '@app/store/sizes/actions';
import { openCategory } from '@app/store/catalogue/thunks';
import { useEffect, useRef } from 'react';
import cn from 'classnames';
import './style.scss';
import CatalogMobileTitleNavigation from '@app/components/CatalogMobileTitleNavigation/CatalogMobileTitleNavigation';
import { ServiceListProps } from './ServiceListProps';
import ServiceListLoader from './ServiceListLoader';
import { Service } from '@app/models/Service/Service.interface';
import { FROM_SUPERAPP } from '@app/constants/fromParams';
import { useFocusOnLocationChange } from '@app/hooks/useFocusOnLocationChange';
import { getCategoryById } from '@app/store/catalogue/selectors';
import { MetricParam, useSpatz } from '@app/hooks/useSpatz';

const ServiceList: React.FC<ServiceListProps> = ({
  services,
  lastOpenedCategory,
  openCategory,
  isLoading,
  groups,
  expandedElements,
  addExpandedElement,
  clearExpandedElements,
  windowWidth,
  isPopular,
  isSuperApp,
  catalogue,
  setServicesHeight,
}) => {
  const listRef = useRef<HTMLDivElement>(null);

  const currentCategory = catalogue.lastOpenedCategory;
  const parentId = currentCategory?.parentId;
  const parentCategory = useSelector(getCategoryById(parentId));
  const { sendSpatzStats } = useSpatz({
    Services: { catalog: { Главная: { 'клик на группу разделов': {} } } },
  });

  const onClickHandler = () => {
    if (currentCategory && parentCategory) {
      const data: MetricParam = {
        click: {
          'Название раздела': currentCategory.title,
          parent: parentCategory.title,
        },
      };
      sendSpatzStats(data);
    }
    openCategory(currentCategory?.parentId);
  };

  function backPath() {
    const pathArr = lastOpenedCategory?.routePath.split('/');
    const path = pathArr?.slice(0, pathArr.length - 2);
    const url = path?.join('/') + '/';
    return url;
  }

  useEffect(() => {
    if (listRef?.current) {
      setServicesHeight(listRef.current.clientHeight); // Успевает сработать при пустом services
      setTimeout(() => {
        if (listRef?.current) {
          setServicesHeight(listRef.current.clientHeight); // Срабатывает в остальных случаях при перестроении DOM
        }
      }, 300);
    }
  }, [services, expandedElements, isLoading, setServicesHeight]);

  const handleSubServiceClick = (subService: Service) => () => {
    clearExpandedElements();
    if (!expandedElements.includes(subService.id)) {
      addExpandedElement(subService.id);
    }
  };

  useFocusOnLocationChange(catalogue, windowWidth);

  return (
    <div className="mos-catalog_service-wrap">
      {(windowWidth > 1023 ||
        isPopular ||
        (lastOpenedCategory &&
          lastOpenedCategory.elementsType === 'services' &&
          windowWidth < 1024) ||
        (lastOpenedCategory &&
          lastOpenedCategory.elementsType === 'mixed' &&
          isPopular &&
          windowWidth < 1024)) && (
        <div>
          {lastOpenedCategory &&
            lastOpenedCategory.elementsType === 'mixed' && (
              <div className="mos-catalog_service-title">Каталог услуг</div>
            )}
          {lastOpenedCategory &&
            lastOpenedCategory.elementsType !== 'mixed' && (
              <Link
                data-testid="backToCategoryButton"
                to={backPath()}
                aria-label={`Категория ${parentCategory?.title}. Вернуться обратно`}
                onClick={onClickHandler}
                className="mos-catalog_service-title mos-catalog_category-title_back"
              >
                {parentCategory?.title}
              </Link>
            )}
          {!isSuperApp &&
            ((lastOpenedCategory?.children &&
              lastOpenedCategory.children.length > 0) ||
              isPopular) && (
              <CatalogMobileTitleNavigation></CatalogMobileTitleNavigation>
            )}
          <div
            className={cn('mos-service-list', {
              'mos-service-popular-list':
                lastOpenedCategory &&
                lastOpenedCategory.elementsType === 'mixed',
            })}
            ref={listRef}
            role="navigation"
            aria-label="Список услуг"
            tabIndex={-1}
          >
            {!isLoading &&
              groups &&
              groups.map((group) => (
                <ServiceGroupItem group={group} key={group.id} />
              ))}
            {!isLoading &&
              services &&
              services.map((service) => {
                if (service?.subServices && service.subServices.length > 0) {
                  return (
                    <SubService
                      key={service.id}
                      service={service}
                      isActive={expandedElements.includes(service.id)}
                      handleClick={handleSubServiceClick(service)}
                    />
                  );
                } else {
                  return <ServiceItem key={service.id} service={service} />;
                }
              })}
            {isLoading && <ServiceListLoader />}
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = (state: AppState) => ({
  isLoading: state.catalogue.isLoading,
  isPopular: state.catalogue.isPopular,
  groups: state.catalogue.groups,
  services: state.catalogue.services,
  lastOpenedCategory: state.catalogue.lastOpenedCategory,
  expandedElements: state.catalogue.expandedElements,
  windowWidth: state.sizes.windowWidth,
  catalogue: state.catalogue,
  isSuperApp: state.catalogue.fromRoute === FROM_SUPERAPP,
});

const dispatchProps = {
  addExpandedElement,
  clearExpandedElements,
  openCategory,
  setServicesHeight,
};

const ServiceListConnected = connect(
  mapStateToProps,
  dispatchProps
)(ServiceList);

export default ServiceListConnected;
